// MediaLibraryPage — global view of all media files across the editorial
// library. Pulls every article (up to 500), aggregates their cover,
// hero_video_file and media_assets into a flat list, then filters and
// sorts in-memory.
//
// Each item shows where it's used — clicking an asset opens a modal with
// a preview and a list of articles linking it. The "Delete unused"
// action removes the file from its source article via PB's `media_assets-`
// notation (works for media_assets only — cover and hero are 1:1 file
// fields and have to be cleared by clearing the article field).

import { useEffect, useMemo, useState } from "react";
import { list, fileUrl, updateRecord } from "../lib/pb";
import { navigate } from "../App";
import { useT, type T } from "../lib/i18n";
import type { Article } from "../lib/types";

type MediaKind = "image" | "video" | "audio" | "other";

interface MediaItem {
  filename: string;
  articleId: string;
  articleTitle: string;
  source: "cover" | "hero" | "media_assets";
  kind: MediaKind;
  created: string; // article.created proxy
}

function classifyKind(name: string): MediaKind {
  const n = name.toLowerCase();
  if (/\.(jpe?g|png|webp|avif|gif|heic)$/.test(n)) return "image";
  if (/\.(mp4|webm|mov|m4v|avi)$/.test(n)) return "video";
  if (/\.(mp3|m4a|wav|ogg|flac)$/.test(n)) return "audio";
  return "other";
}

export default function MediaLibraryPage() {
  const t = useT();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState<MediaKind | "all">("all");
  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState<"newest" | "oldest" | "name">("newest");
  const [picked, setPicked] = useState<MediaItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await list<Article>("articles", { perPage: 500, sort: "-updated" });
      setArticles(r.items);
    } catch (e) {
      alert(`Failed to load media: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  // Flatten all media references across articles.
  const allItems = useMemo<MediaItem[]>(() => {
    const out: MediaItem[] = [];
    for (const a of articles) {
      if (a.cover) {
        out.push({
          filename: a.cover, articleId: a.id, articleTitle: a.title,
          source: "cover", kind: classifyKind(a.cover), created: a.created,
        });
      }
      if (a.hero_video_file) {
        out.push({
          filename: a.hero_video_file, articleId: a.id, articleTitle: a.title,
          source: "hero", kind: classifyKind(a.hero_video_file), created: a.created,
        });
      }
      for (const f of a.media_assets || []) {
        out.push({
          filename: f, articleId: a.id, articleTitle: a.title,
          source: "media_assets", kind: classifyKind(f), created: a.created,
        });
      }
    }
    return out;
  }, [articles]);

  // Group by filename to find duplicate usages (rare but possible).
  const usageByFilename = useMemo(() => {
    const map = new Map<string, MediaItem[]>();
    for (const it of allItems) {
      const list = map.get(it.filename) || [];
      list.push(it);
      map.set(it.filename, list);
    }
    return map;
  }, [allItems]);

  const filtered = useMemo<MediaItem[]>(() => {
    let out = allItems;
    if (filterKind !== "all") out = out.filter((m) => m.kind === filterKind);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(
        (m) =>
          m.filename.toLowerCase().includes(q) ||
          m.articleTitle.toLowerCase().includes(q),
      );
    }
    // Deduplicate by filename — first occurrence wins.
    const seen = new Set<string>();
    out = out.filter((m) => {
      if (seen.has(m.filename)) return false;
      seen.add(m.filename);
      return true;
    });
    if (sortMode === "newest") out = [...out].sort((a, b) => b.created.localeCompare(a.created));
    else if (sortMode === "oldest") out = [...out].sort((a, b) => a.created.localeCompare(b.created));
    else if (sortMode === "name") out = [...out].sort((a, b) => a.filename.localeCompare(b.filename));
    return out;
  }, [allItems, filterKind, search, sortMode]);

  async function deleteFromArticle(item: MediaItem) {
    if (item.source !== "media_assets") {
      alert("Cover and hero files are cleared from the article's settings, not the library.");
      return;
    }
    if (!confirm(`Remove "${item.filename}" from "${item.articleTitle}"?`)) return;
    setDeleting(true);
    try {
      const fd = new FormData();
      // PB removal notation.
      fd.append("media_assets-", item.filename);
      await updateRecord("articles", item.articleId, fd);
      setPicked(null);
      await load();
    } catch (e) {
      alert(`Delete failed: ${(e as Error).message}`);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t.mediaTitle}</h1>
        <p className="page-subtitle">{t.mediaCount(filtered.length, allItems.length)}</p>
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {(["all", "image", "video", "audio", "other"] as const).map((k) => (
            <button key={k} onClick={() => setFilterKind(k)} className={`pill ${filterKind === k ? "active" : ""}`}>
              {mediaKindLabel(t, k)}
            </button>
          ))}
        </div>
        <select value={sortMode} onChange={(e) => setSortMode(e.target.value as typeof sortMode)} className="select" style={{ width: 160 }}>
          <option value="newest">{t.mediaSortDate}</option>
          <option value="oldest">{t.mediaSortOldest}</option>
          <option value="name">{t.mediaSortName}</option>
        </select>
        <input
          type="search"
          placeholder={t.mediaSearch}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input"
          style={{ marginLeft: "auto", width: 280 }}
        />
      </div>

      {loading ? (
        <div className="spinner-line">{t.loading}</div>
      ) : filtered.length === 0 ? (
        <div className="empty">{t.mediaNothingFound}</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
          {filtered.map((m) => (
            <button
              key={`${m.articleId}/${m.filename}`}
              onClick={() => setPicked(m)}
              className="card card-hover"
              style={{ padding: 0, overflow: "hidden", textAlign: "left", display: "flex", flexDirection: "column" }}
            >
              <div style={{ aspectRatio: "1/1", background: "var(--bg-3)", overflow: "hidden", position: "relative" }}>
                {m.kind === "image" ? (
                  <img
                    src={fileUrl("articles", m.articleId, m.filename, "240x240")}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    loading="lazy"
                  />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-3)", fontSize: 28 }}>
                    {m.kind === "video" ? "▶" : m.kind === "audio" ? "♪" : "▢"}
                  </div>
                )}
                <span
                  style={{
                    position: "absolute", top: 6, left: 6,
                    padding: "1px 6px", fontSize: 9, fontWeight: 600,
                    background: "rgba(28,28,30,0.7)", color: "var(--text-1)",
                    borderRadius: 3, letterSpacing: "0.06em", textTransform: "uppercase",
                  }}
                >
                  {m.kind}
                </span>
              </div>
              <div style={{ padding: "8px 10px" }}>
                <div className="mono" style={{ fontSize: 11, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.filename}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.articleTitle || "(untitled)"}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {picked && (
        <MediaModal
          t={t}
          item={picked}
          usages={usageByFilename.get(picked.filename) || [picked]}
          deleting={deleting}
          onClose={() => setPicked(null)}
          onDelete={() => deleteFromArticle(picked)}
          onGoToArticle={(id) => { setPicked(null); navigate(`/article/${id}`); }}
        />
      )}
    </div>
  );
}

function mediaKindLabel(t: T, k: MediaKind | "all"): string {
  switch (k) {
    case "all": return t.mediaFilterAll;
    case "image": return t.mediaFilterImage;
    case "video": return t.mediaFilterVideo;
    case "audio": return t.mediaFilterAudio;
    case "other": return t.mediaFilterOther;
  }
}

function MediaModal({
  t, item, usages, deleting, onClose, onDelete, onGoToArticle,
}: {
  t: T;
  item: MediaItem;
  usages: MediaItem[];
  deleting: boolean;
  onClose: () => void;
  onDelete: () => void;
  onGoToArticle: (id: string) => void;
}) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 720, maxHeight: "90vh" }}
      >
        <div className="modal-header">
          <span className="modal-title">{item.filename}</span>
          <button onClick={onClose} className="btn btn-ghost btn-sm">×</button>
        </div>
        <div className="modal-body" style={{ display: "flex", gap: 18 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ aspectRatio: "16/9", background: "var(--bg-0)", borderRadius: "var(--radius-md)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {item.kind === "image" ? (
                <img
                  src={fileUrl("articles", item.articleId, item.filename, "800x600")}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : item.kind === "video" ? (
                <video
                  src={fileUrl("articles", item.articleId, item.filename)}
                  controls
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : item.kind === "audio" ? (
                <audio src={fileUrl("articles", item.articleId, item.filename)} controls style={{ width: "80%" }} />
              ) : (
                <span className="muted">{item.kind}</span>
              )}
            </div>
            <a
              href={fileUrl("articles", item.articleId, item.filename)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "inline-block", marginTop: 10, fontSize: 12 }}
            >
              {t.mediaOpenOriginal} →
            </a>
          </div>
          <div style={{ width: 260, flexShrink: 0, display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="field">
              <span className="field-label">{t.mediaFilterKind}</span>
              <span style={{ fontSize: 12, color: "var(--text-1)" }}>{mediaKindLabel(t, item.kind)}</span>
            </div>
            <div className="field">
              <span className="field-label">{t.mediaUsedIn}</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {usages.map((u) => (
                  <button
                    key={u.articleId + u.source}
                    onClick={() => onGoToArticle(u.articleId)}
                    className="btn btn-secondary btn-sm"
                    style={{ justifyContent: "flex-start", textAlign: "left" }}
                  >
                    <span style={{ fontSize: 11, color: "var(--accent)", marginRight: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      {u.source}
                    </span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {u.articleTitle || "(untitled)"}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          {item.source === "media_assets" && (
            <button onClick={onDelete} disabled={deleting} className="btn btn-danger">
              {deleting ? t.mediaRemoving : t.mediaRemoveFromArticle}
            </button>
          )}
          <button onClick={onClose} className="btn btn-secondary">{t.close}</button>
        </div>
      </div>
    </div>
  );
}
